import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';

import { Icon } from './Icon';
import { useFieldWiring } from './useFieldWiring';

type NativeInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'className' | 'id' | 'aria-describedby' | 'aria-invalid'
>;

export interface TextInputProps extends NativeInputProps {
  /** A short unit or currency marker rendered inside the control's trailing edge. */
  readonly suffix?: string;
}

/**
 * A single-line text control.
 *
 * It deliberately does not accept `id`, `aria-describedby` or `aria-invalid`:
 * those belong to the {@link FormField} wrapping it, and letting a caller pass
 * them is how a label quietly stops pointing at its control.
 *
 * @param props see {@link TextInputProps}
 * @returns the input element
 */
export function TextInput({ suffix, ...rest }: TextInputProps): React.JSX.Element {
  const { controlId, describedBy, invalid, required } = useFieldWiring();

  return (
    <span className="fo-input" data-invalid={invalid ? '' : undefined}>
      <input
        {...rest}
        id={controlId}
        className="fo-input__control"
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        required={required || rest.required}
      />
      {suffix === undefined ? null : (
        <span className="fo-input__suffix" aria-hidden="true">
          {suffix}
        </span>
      )}
    </span>
  );
}

export type TextAreaProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'className' | 'id' | 'aria-describedby' | 'aria-invalid'
>;

/**
 * A multi-line text control, wired the same way as {@link TextInput}.
 *
 * @param props standard textarea attributes minus the ones the field owns
 * @returns the textarea element
 */
export function TextArea(props: TextAreaProps): React.JSX.Element {
  const { controlId, describedBy, invalid, required } = useFieldWiring();

  return (
    <textarea
      {...props}
      id={controlId}
      className="fo-textarea"
      rows={props.rows ?? 4}
      aria-describedby={describedBy}
      aria-invalid={invalid || undefined}
      required={required || props.required}
    />
  );
}

export interface SelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'className' | 'id' | 'aria-describedby' | 'aria-invalid'
> {
  /** The options. */
  readonly children: ReactNode;
}

/**
 * A select.
 *
 * Native, on purpose. A custom listbox is the right answer when the choices
 * need search or rich rows — that is what {@link Combobox} is for — but for a
 * closed set of short options the platform control already handles touch,
 * screen readers, type-ahead and small viewports better than a reimplementation
 * would.
 *
 * @param props see {@link SelectProps}
 * @returns the select element
 */
export function Select({ children, ...rest }: SelectProps): React.JSX.Element {
  const { controlId, describedBy, invalid, required } = useFieldWiring();

  return (
    <span className="fo-select" data-invalid={invalid ? '' : undefined}>
      <select
        {...rest}
        id={controlId}
        className="fo-select__control"
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        required={required || rest.required}
      >
        {children}
      </select>
      <Icon name="chevron-down" className="fo-select__chevron" />
    </span>
  );
}
